import Utils from "../Utils.js";
import firebase from "firebase/app";
import "firebase/auth";
import * as Sentry from "@sentry/react";

const db = firebase.firestore();
const collection = db.collection("favorites");

// favorites {
// 	id
// 	userId
// 	createdAt
// 	cardId: the associated card which was favorited
// 	prompt: the user's prompt
// 	source: source of the card
// 	content: string of the content (only one component of a card from an array)
// }

class Favorite {
  constructor(id = null, data, fetched = true) {
    if (fetched && !id) {
      throw "ID is required from a fetched object";
    }

    if (fetched) {
      this.id = id;
    } else {
      this.id = Utils.generateId();
    }

    this.fetched = fetched;

    if (!data.userId) {
      throw "Data: userId is required";
    }
    if (!data.cardId) {
      throw "Data: cardId is required";
    }
    if (!data.source) {
      throw "Data: source is required";
    }
    if (!data.content) {
      throw "Data: content is required";
    }
    if (!data.documentId) {
      throw "Data: documentId is required";
    }

    this.data = data;

    this.deleted = false;

    if (!this.data.createdAt) {
      this.data.createdAt = new Date();
    }

    if (!(this.data.createdAt instanceof Date)) {
      // convert from firebase format
      this.data.createdAt = Utils.dateFromTimestamp(this.data.createdAt);
    }
  }

  get content() {
    return this.data.content;
  }

  get cardId() {
    return this.data.cardId;
  }

  get prompt() {
    return this.data.prompt;
  }

  get source() {
    return this.data.source;
  }

  get userId() {
    return this.data.userId;
  }

  get path() {
    return this.data.path;
  }

  static async createFromCard(card, content) {
    if (!card.fetched) {
      throw "Cannot create from a card that hasn't been fetched or saved server side";
    }

    let prompt;

    switch (card.data.source) {
      case "description":
        prompt = card.data.object;
        break;
      case "multiverse":
      case "visualize":
      case "guidedWormhole":
      case "twist":
      case "character":
      case "dialogue":
      case "summary":
      case "shrinkRay":
      case "expand":
      case "whatIf":
      case "nextScene":
      case "analyze":
      case "poem":
      case "rewrite":
      case "list":
        prompt = card.data.prompt;
        break;
      default:
        // custom functions
        prompt = card.data.prompt;
        break;
    }

    let path = card.pathFromContent(content);

    if (!prompt) {
      Sentry.captureMessage("Favorite.createFromCard: empty prompt", {
        level: "warning",
        tags: {
          cardId: card.id,
          source: card.data.source,
          userId: card.data.userId,
          documentId: card.data.documentId,
        },
        extra: {
          cardData: card.data,
        },
      });
    }

    let data = {
      userId: card.data.userId,
      cardId: card.id,
      documentId: card.data.documentId,
      prompt: prompt ?? "",
      source: card.data.source,
      content: content,
      path: path,
    };

    let docRef = await collection.add(data);

    return new Favorite(docRef.id, data);
  }

  async delete() {
    await collection.doc(this.id).delete();
  }

  // Returns the favorite if it exists on server, else false
  static async exists(cardId, content, userId) {
    const max = 700;

    let snap = collection.where("cardId", "==", cardId).where("userId", "==", userId);

    if (content.length > max) {
      // If the content is too long then firestore won't let us query by it
      // so instead we query the beginning substring >= and < the substring
      // with the last character incremented by one code point
      // https://stackoverflow.com/questions/46573804/firestore-query-documents-startswith-a-string
      let front = content.slice(0, max - 1);
      let last = content.slice(max - 1, max);
      let end = front + String.fromCharCode(last.charCodeAt(0) + 1);

      snap = snap.where("content", ">=", front + last).where("content", "<", end);
    } else {
      snap = snap.where("content", "==", content);
    }

    snap = await snap.limit(1).get();

    if (snap.empty) {
      return false;
    } else {
      let doc = snap.docs[0];
      return new Favorite(doc.id, doc.data());
    }
  }

  async save() {}

  setFetched(id) {
    if (!id) {
      throw "Must provide an ID";
    }

    this.id = id;
    this.fetched = true;
  }

  // deletes the favorite firebase
  async delete() {
    await collection.doc(this.id).delete();
    this.deleted = true;
  }
}

export default Favorite;
